import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiDollarSign, FiTrendingUp, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { CostBreakdown } from './CostBreakdown';
import { CostChart } from './CostChart';
import { CostOptimizationRecommendations } from './CostOptimizationRecommendations';
import { getOptimizationRecommendations } from '../../api/cost';
import { CostBreakdownItem, UnsupportedResource } from '../../api/cost';

interface CostAnalysisDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  breakdown: CostBreakdownItem[];
  unsupported: UnsupportedResource[];
  resources?: any[];
}

/**
 * Drawer d'analyse détaillée des coûts
 * Affiche les graphiques, le breakdown et les recommandations
 */
export const CostAnalysisDrawer: React.FC<CostAnalysisDrawerProps> = ({
  isOpen,
  onClose,
  totalMonthly,
  totalHourly,
  currency,
  breakdown,
  unsupported,
  resources = []
}) => {
  const { t } = useTranslation('canvas');
  const [activeTab, setActiveTab] = useState<'overview' | 'breakdown' | 'chart' | 'optimization'>('overview');
  const [optimizationData, setOptimizationData] = useState<any>(null);
  const [loadingOptimization, setLoadingOptimization] = useState(false);

  // Charger les recommandations d'optimisation
  useEffect(() => {
    if (isOpen && resources.length > 0 && !optimizationData) {
      loadOptimizations();
    }
  }, [isOpen, resources]);

  const loadOptimizations = async () => {
    setLoadingOptimization(true);
    try {
      const data = await getOptimizationRecommendations(resources, {
        includeReservedInstances: true,
        includeSpotInstances: true
      });
      setOptimizationData(data);
    } catch (error) {
      console.error('Erreur lors du chargement des optimisations:', error);
    } finally {
      setLoadingOptimization(false);
    }
  };

  if (!isOpen) return null;

  const budget = 5000; // TODO: Récupérer le budget du projet
  const budgetUsage = budget > 0 ? (totalMonthly / budget) * 100 : 0;
  const forecast = totalMonthly * 1.07;
  const totalSavingsPotential = optimizationData?.totalSavings || 0;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1999,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '80%',
          maxWidth: '1200px',
          backgroundColor: '#f9fafb',
          zIndex: 2000,
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', margin: 0 }}>
              {t('analysis.title')}
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {t('analysis.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FiX size={24} color="#6b7280" />
          </button>
        </div>

        {/* KPIs Row */}
        <div style={{
          padding: '24px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px'
        }}>
          {/* Coût Estimé */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FiDollarSign size={20} color="#3b82f6" />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>{t('analysis.kpis.estimatedCost')}</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>
              {totalMonthly.toFixed(0)} {currency}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {totalHourly.toFixed(2)} {currency}{t('cost.perHour')}
            </div>
          </div>

          {/* Budget Usage */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FiTrendingUp size={20} color="#8b5cf6" />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>{t('analysis.kpis.budgetUsage')}</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>
              {budgetUsage.toFixed(1)}%
            </div>
            <div style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#e5e7eb',
              borderRadius: '2px',
              marginTop: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(budgetUsage, 100)}%`,
                height: '100%',
                backgroundColor: budgetUsage > 95 ? '#ef4444' : budgetUsage > 80 ? '#f59e0b' : '#10b981',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Prévision */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FiTrendingUp size={20} color="#f59e0b" />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>{t('analysis.kpis.forecast')}</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>
              {forecast.toFixed(0)} {currency}
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              <span style={{ color: forecast > budget ? '#ef4444' : '#10b981' }}>
                {forecast > budget ? '+' : ''}{(forecast - budget).toFixed(0)} {currency}
              </span>
              <span style={{ color: '#6b7280' }}> {t('analysis.kpis.vsBudget')}</span>
            </div>
          </div>

          {/* Économies Potentielles */}
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '8px',
            color: '#ffffff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FiAlertTriangle size={20} />
              <span style={{ fontSize: '12px', fontWeight: 600 }}>{t('analysis.kpis.potentialSavings')}</span>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700 }}>
              {totalSavingsPotential.toFixed(0)} {currency}
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
              {optimizationData?.recommendations?.length || 0} {t('analysis.kpis.recommendations')}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          padding: '0 24px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '8px'
        }}>
          {[
            { key: 'overview', label: t('analysis.tabs.overview') },
            { key: 'breakdown', label: t('analysis.tabs.breakdown') },
            { key: 'chart', label: t('analysis.tabs.chart') },
            { key: 'optimization', label: t('analysis.tabs.optimization') }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              style={{
                padding: '12px 20px',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === tab.key ? '#3b82f6' : '#6b7280',
                fontWeight: activeTab === tab.key ? 600 : 500,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Chart */}
              {breakdown.length > 0 && (
                <CostChart
                  breakdown={breakdown}
                  currency={currency}
                  totalMonthly={totalMonthly}
                />
              )}

              {/* Summary Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px'
              }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                    {t('analysis.overview.resources')}
                  </h3>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#3b82f6' }}>
                    {breakdown.length}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {t('analysis.overview.resourcesEstimated')}
                  </div>
                  {unsupported.length > 0 && (
                    <div style={{ fontSize: '14px', color: '#f59e0b', marginTop: '8px' }}>
                      {unsupported.length} {t('analysis.overview.unsupported')}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                    {t('analysis.overview.optimization')}
                  </h3>
                  {loadingOptimization ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FiRefreshCw size={16} className="animate-spin" />
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>{t('analysis.overview.loading')}</span>
                    </div>
                  ) : optimizationData ? (
                    <>
                      <div style={{ fontSize: '32px', fontWeight: 700, color: '#10b981' }}>
                        {optimizationData.savingsPercentage.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                        {t('analysis.overview.possibleSavings')}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('analysis.overview.noData')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'breakdown' && breakdown.length > 0 && (
            <CostBreakdown
              breakdown={breakdown}
              currency={currency}
              totalMonthly={totalMonthly}
              totalHourly={totalHourly}
            />
          )}

          {activeTab === 'chart' && breakdown.length > 0 && (
            <CostChart
              breakdown={breakdown}
              currency={currency}
              totalMonthly={totalMonthly}
            />
          )}

          {activeTab === 'optimization' && (
            <>
              {loadingOptimization ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px',
                  gap: '16px'
                }}>
                  <FiRefreshCw size={48} style={{ color: '#3b82f6' }} className="animate-spin" />
                  <span style={{ fontSize: '16px', color: '#6b7280' }}>
                    {t('analysis.optimization.analyzing')}
                  </span>
                </div>
              ) : optimizationData && optimizationData.recommendations?.length > 0 ? (
                <CostOptimizationRecommendations
                  optimizationData={optimizationData}
                  currency={currency}
                  loading={loadingOptimization}
                />
              ) : (
                <div style={{
                  padding: '60px',
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <FiAlertTriangle size={48} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                    {t('analysis.optimization.noRecommendations')}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    {t('analysis.optimization.noRecommendationsDescription')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Animation */}
        <style>{`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};
